'use client'
import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { Settings, SettingsContextType } from '@/types'
import { SettingsItem, SupportedLanguage, SupportedReligion } from '@/enum'
import { SETTINGS_LOCAL_STORAGE_KEY } from '@/constants/keys'

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

const defaultSettings: Settings = {
  language: SupportedLanguage.English,
  religion: SupportedReligion.Hinduism,
}

type SettingsProviderProps = {
  children?: React.ReactNode
}

const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [showProgress, setShowProgress] = useState<boolean>(false)
  const [settings, setSettings] = useState<Settings>(defaultSettings)
  const isLoaded = useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedSettings = localStorage.getItem(SETTINGS_LOCAL_STORAGE_KEY)
      if (storedSettings) {
        try {
          setSettings(JSON.parse(storedSettings))
        } catch (e) {
          console.error('Failed to parse settings from localStorage', e)
        }
      }
    }
    isLoaded.current = true
  }, [])

  useEffect(() => {
    if (isLoaded.current && typeof window !== 'undefined') {
      localStorage.setItem(SETTINGS_LOCAL_STORAGE_KEY, JSON.stringify(settings))
    }
  }, [settings])

  const updateSetting = (key: SettingsItem, value: string) => {
    if (key === SettingsItem.LANGUAGE) {
      setShowProgress(true)
    }
    setSettings((prevSettings) => ({ ...prevSettings, [key]: value }))
  }

  return <SettingsContext.Provider value={{ settings, updateSetting, showProgress, setShowProgress }}>{children}</SettingsContext.Provider>
}

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

export default SettingsProvider
